import { Voice } from '@/services/voices-service/voices.service.types';
import { CloseOutlined, PauseCircleOutlined, PlayCircleOutlined, SoundOutlined, SwapOutlined } from '@ant-design/icons';
import { Flex, Typography } from 'antd';
import { useRef, useState } from 'react';
import { Button } from '@/components/button/button';
import { VoiceSelectorDrawer } from '@/components/voice-selector-drawer/voice-selector-drawer';
import { useTranslation } from 'react-i18next';

type VoiceFieldProps = {
  value?: string | null;
  onChange?: (value: string | null) => void;
  companyId: number | undefined;
  defaultSearch?: string;
  // ISO 639-1 prefix; pre-applies the language filter in the picker drawer
  // so e.g. the German row's voice browse only shows German voices.
  defaultLanguage?: string;
};

export function VoiceField({ value, onChange, companyId, defaultSearch, defaultLanguage }: VoiceFieldProps) {
  const { t } = useTranslation(['voices', 'common']);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [voiceName, setVoiceName] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const handleSelect = (voice: Voice) => {
    onChange?.(voice.voice_id);
    setVoiceName(voice.name);
    setPreviewUrl(voice.preview_url);
  };

  const handleClear = () => {
    onChange?.(null);
    setVoiceName(null);
    setPreviewUrl(null);
    audioRef.current?.pause();
    setIsPlaying(false);
  };

  const handlePlay = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }
    if (!previewUrl) return;
    audioRef.current?.pause();
    const audio = new Audio(previewUrl);
    audio.onended = () => setIsPlaying(false);
    audio.play();
    audioRef.current = audio;
    setIsPlaying(true);
  };

  return (
    <>
      {value ? (
        <Flex align="center" gap={8}>
          <SoundOutlined />
          <Typography.Text style={{ flex: 1 }}>
            {voiceName || value}
          </Typography.Text>
          {previewUrl && (
            <Button
              type="text"
              size="small"
              icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={handlePlay}
            />
          )}
          <Button
            type="text"
            size="small"
            icon={<SwapOutlined />}
            onClick={() => setDrawerOpen(true)}
          >
            {t('voices:field.change')}
          </Button>
          <Button
            type="text"
            size="small"
            danger
            icon={<CloseOutlined />}
            onClick={handleClear}
          >
            {t('common:actions.clear')}
          </Button>
        </Flex>
      ) : (
        <Button
          icon={<SoundOutlined />}
          onClick={() => setDrawerOpen(true)}
        >
          {t('voices:field.browse')}
        </Button>
      )}

      <VoiceSelectorDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSelect={handleSelect}
        companyId={companyId}
        selectedVoiceId={value}
        defaultSearch={defaultSearch}
        defaultLanguage={defaultLanguage}
      />
    </>
  );
}
